import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { eventsAPI } from '../../../api/events';
import { TIMEFRAME_UI_TO_CANONICAL as SHARED_TIMEFRAME_UI_TO_CANONICAL } from '../../../utils/timeframes';

// Re-export the shared mapping so existing callers (marketDetailPage,
// hook tests) keep working with their current import path. The
// constant itself moved to src/utils/timeframes.js so the api-client
// modules can import it without depending on this hook — bugbot
// flagged the previous duplication on PR #420.
export const TIMEFRAME_UI_TO_CANONICAL = SHARED_TIMEFRAME_UI_TO_CANONICAL;

// All cycle-related event types we pull for the chart overlay. The library
// emits the full lifecycle (provisional → developing.* → confirmed, plus
// failed / superseded / sweep / higher-low / lower-low), and we need the
// whole set to reconstruct the "latest state per pivot" used to render
// DEVELOPING vs CL triangles.
const CYCLE_EVENT_TYPES = [
  'cycle.low.provisional',
  'cycle.low.developing.33',
  'cycle.low.developing.66',
  'cycle.low.developing.100',
  'cycle.low.confirmed',
  'cycle.low.superseded',
  'cycle.low.failed',
  'cycle.low.higher_low',
  'cycle.low.lower_low',
  'cycle.low.sweep',
  'cycle.high.provisional',
  'cycle.high.developing.33',
  'cycle.high.developing.66',
  'cycle.high.developing.100',
  'cycle.high.confirmed',
  'cycle.high.superseded',
  'cycle.high.failed',
  'cycle.high.higher_high',
  'cycle.high.lower_high',
  'cycle.high.sweep',
];

const toNumberOrNull = (value) => {
  if (value === null || value === undefined) {
    return null;
  }
  const num = Number(value);
  return Number.isFinite(num) ? num : null;
};

const toTimestampOrNull = (value) => {
  if (value === null || value === undefined) {
    return null;
  }
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  const numeric = Number(value);
  if (Number.isFinite(numeric)) {
    return numeric;
  }
  const parsed = new Date(value).getTime();
  return Number.isFinite(parsed) ? parsed : null;
};

// Events come off the /events endpoint with the payload flattened to the
// top level (mapIndicatorEventToStoredEvent does that write-side). Undo
// the flattening into the library-shape `{ id, t, index, payload: { … } }`
// the chart components already know how to consume — that way we don't
// need to rewrite registerEvents or the tooltip logic downstream.
const toLibraryEvent = (stored) => {
  if (!stored || typeof stored !== 'object') {return null;}
  const eventType = stored.eventType || stored.indicatorEventId;
  if (typeof eventType !== 'string') {return null;}

  const t = toTimestampOrNull(stored.eventTimestamp ?? stored.sourceTimestamp ?? stored.t);
  if (t === null) {return null;}
  const index = toNumberOrNull(stored.indicatorEventIndex);
  const pivotIndex = toNumberOrNull(stored.pivotIndex);
  const pivotTime = toTimestampOrNull(stored.pivotTime);
  const confirmIndex = toNumberOrNull(stored.confirmIndex);
  const confirmTime = toTimestampOrNull(stored.confirmTime);
  const price = toNumberOrNull(stored.price ?? stored.low ?? stored.high);

  // A cycle event without a pivotIndex is unanchorable on the chart —
  // buildConfirmedPivots can't produce a CL triangle for it and
  // buildLiveState can't position the DEVELOPING overlay. Drop it
  // at the boundary so downstream is simpler and, critically, so
  // an API response that strips pivot fields (pre-#384 backend or
  // a future regression) produces zero events instead of a wall
  // of junk. Otherwise useBackendLow would gate ON (events exist)
  // while every consumer silently discarded them → chart renders
  // neither backend nor client-side overlays, because we never
  // fall back. (Bugbot caught this on the third review.)
  if (pivotIndex === null) {return null;}

  return {
    id: eventType,
    name: stored.name || eventType,
    description: stored.description || '',
    t,
    index: index ?? (confirmIndex ?? pivotIndex ?? 0),
    payload: {
      pivotIndex,
      pivotTime,
      confirmIndex,
      confirmTime,
      price,
      status: stored.status,
      pivotType: stored.pivotType,
      totalScore: toNumberOrNull(stored.totalScore),
      currentScore: toNumberOrNull(stored.currentScore),
      maxPossibleScore: toNumberOrNull(stored.maxPossibleScore),
      pivotConfidencePct: toNumberOrNull(stored.pivotConfidencePct),
      confidencePct: toNumberOrNull(stored.confidencePct),
      timeframe: stored.timeframe,
      state: stored.state,
    },
    meta: {
      state: stored.state,
      eventTimestamp: stored.eventTimestamp,
    },
  };
};

// Reduce the library-shape cycle-event stream into the shape the chart's
// "CL N" triangle renderer wants: one entry per confirmed pivot with
// `pivotIndex / pivotTime / low (or high) / confirmIndex / confirmTime /
// totalScore`.
//
// Dedup is keyed on `pivotTime`, NOT `pivotIndex`. The scheduled-jobs
// lambda emits cycle events with `pivotIndex` set to the pivot's bar
// position *in the rolling candle window the run was using at emission
// time*. That window shifts across runs as new bars close, so the same
// physical pivot bar gets emitted with a slightly different pivotIndex
// every run — keying on pivotIndex fails to merge them and we paint two
// (or more) overlapping triangles at the same X-coordinate. pivotTime
// is the epoch-ms timestamp of the pivot's bar, invariant across runs,
// so it's the stable identity.
//
// Among events sharing a pivotTime we keep the one with the latest `t`
// (or eventTimestamp) — that's the most recent emission, reflecting the
// state machine's freshest view of the pivot.
const buildConfirmedPivots = (events, side) => {
  const byPivotTime = new Map();
  const confirmedId = side === 'low' ? 'cycle.low.confirmed' : 'cycle.high.confirmed';
  for (const event of events) {
    if (event.id !== confirmedId) {continue;}
    const pivotIndex = event.payload?.pivotIndex;
    const pivotTime = event.payload?.pivotTime ?? event.t;
    if (!Number.isFinite(pivotIndex) || !Number.isFinite(pivotTime)) {continue;}

    const incoming = {
      pivotIndex,
      pivotTime,
      [side === 'low' ? 'low' : 'high']: event.payload?.price ?? null,
      confirmIndex: event.payload?.confirmIndex ?? event.index,
      confirmTime: event.payload?.confirmTime ?? event.t,
      // Actual emission/detection timestamp — the cron-run bar at the
      // moment the event was written. Distinct from `confirmTime`,
      // which is the latest score-component bar (DSS cross / SMA hold)
      // and frequently collapses to `pivotTime` when those criteria
      // fired retrospectively at the pivot bar itself. Used by the
      // chart to position the top-row "events" marker honestly:
      // showing it at the pivot bar implies "we knew at the time,"
      // which is never true. `event.t` is the library-emitted anchor
      // candle's time (cf. liveCycleEvents.ts ~line 454), preserved
      // by toLibraryEvent above as `eventTimestamp ?? sourceTimestamp`.
      emittedAt: Number.isFinite(event.t) ? event.t : null,
      totalScore: event.payload?.totalScore ?? null,
      __emittedAt: Number.isFinite(event.t) ? event.t : 0,
    };
    const existing = byPivotTime.get(pivotTime);
    if (!existing || incoming.__emittedAt >= existing.__emittedAt) {
      byPivotTime.set(pivotTime, incoming);
    }
  }
  return Array.from(byPivotTime.values())
    .map(({ __emittedAt: _emittedAt, ...rest }) => rest)
    .sort((a, b) => a.pivotTime - b.pivotTime);
};

// Classifies a cycle event id as either "active" (provisional / developing.*)
// or "terminal" (confirmed / failed / superseded). We only want to render a
// DEVELOPING triangle from events whose latest-for-that-pivot is active — a
// pivot that's already been confirmed should be represented by a CL triangle
// via the confirmed-pivots list above, not by a second DEVELOPING overlay.
const CYCLE_ACTIVE_IDS = new Set([
  'cycle.low.provisional',
  'cycle.low.developing.33',
  'cycle.low.developing.66',
  'cycle.low.developing.100',
  'cycle.high.provisional',
  'cycle.high.developing.33',
  'cycle.high.developing.66',
  'cycle.high.developing.100',
]);

// "Terminal" includes confirmation, invalidation, supersession AND the
// post-confirmation comparison/sweep events. higher_low / lower_low /
// sweep all imply the pivot is already confirmed (the library only
// emits them for confirmed pivots) — so they should retire the
// DEVELOPING overlay just like a `.confirmed` would. Without them in
// this set the walk in buildLiveState marks the pivot seen but
// neither returns nor classifies it, then continues to OLDER pivots
// and can resurface a stale developing state from a pivot that's
// already been overtaken — exactly the bug this module was written
// to fix. (Bugbot caught this on initial review of PR #409.)
const CYCLE_TERMINAL_IDS = new Set([
  'cycle.low.confirmed',
  'cycle.low.failed',
  'cycle.low.superseded',
  'cycle.low.higher_low',
  'cycle.low.lower_low',
  'cycle.low.sweep',
  'cycle.high.confirmed',
  'cycle.high.failed',
  'cycle.high.superseded',
  'cycle.high.higher_high',
  'cycle.high.lower_high',
  'cycle.high.sweep',
]);

// Build the equivalent of `liveCycleLowState` the chart expects on the
// `liveCycleLow` prop, but purely from persisted events. The chart's
// DEVELOPING triangle renderer reads `status / pivotIndex / pivotTime /
// low (or high) / pivotConfidencePct / pivotType`, and treats
// `status === 'none'` as "don't render". That's what we want whenever
// the latest event for the most-recent pivot is terminal — a confirmed
// pivot is already drawn as a CL triangle, so the DEVELOPING overlay
// should retreat.
//
// Returns `null` (not `{status:'none'}`) when there's nothing to render.
// The client-side path in marketDetailPage canonicalises its "no active
// pivot" result to `null` before exposing it as `liveCycleLow` (see
// around the useMemo's `state.status === 'none'` branch), and the chart
// component declares `liveCycleLow = null` as its default prop. Keeping
// the backend-sourced and client-sourced shapes identical here is
// important: any downstream consumer that does `if (liveCycleLow)`
// instead of `if (liveCycleLow && liveCycleLow.status !== 'none')`
// would otherwise enter the render path with every positional field
// undefined. Bugbot flagged this on PR #409 review.
const buildLiveState = (events, side) => {
  const priceKey = side === 'low' ? 'low' : 'high';

  // Pick the "most recent pivot" by the highest pivotIndex in the
  // event stream — NOT by newest event timestamp. Event timestamps
  // are unreliable as a recency signal because a post-confirmation
  // event (cycle.*.sweep for an older confirmed pivot, for
  // example) can carry a newer `t` than the developing events of
  // a newer pivot. Walking newest-timestamp-first would hit the
  // sweep's older pivotIndex first, classify it as terminal, and
  // hide the DEVELOPING overlay for the pivot that's actually
  // still forming. pivotIndex is monotonic across the candle
  // stream, so max(pivotIndex) is a robust identifier for the
  // newest pivot in the fetched window. (Bugbot caught this on
  // review — the earlier timestamp-sorted walk was wrong in
  // exactly this "sweep-on-older-pivot during newer-pivot-active"
  // case.)
  let maxPivotIndex = -1;
  for (const event of events) {
    const pivotIndex = event?.payload?.pivotIndex;
    if (!Number.isFinite(pivotIndex)) {continue;}
    if (pivotIndex > maxPivotIndex) {maxPivotIndex = pivotIndex;}
  }
  if (maxPivotIndex < 0) {return null;}

  // Of the events for the newest pivot, pick the one with the
  // latest timestamp — that's the pivot's current state. Among
  // ties, prefer terminal over active so a confirmed-at-same-
  // timestamp-as-developing resolves correctly (the library's
  // state machine emits developing and confirmed together on a
  // same-bar transition).
  let latest = null;
  for (const event of events) {
    if (event?.payload?.pivotIndex !== maxPivotIndex) {continue;}
    if (!latest) {latest = event; continue;}
    const a = Number.isFinite(event.t) ? event.t : 0;
    const b = Number.isFinite(latest.t) ? latest.t : 0;
    if (a > b) {latest = event; continue;}
    if (a === b && CYCLE_TERMINAL_IDS.has(event.id) && !CYCLE_TERMINAL_IDS.has(latest.id)) {
      latest = event;
    }
  }
  if (!latest) {return null;}

  if (CYCLE_TERMINAL_IDS.has(latest.id)) {return null;}
  if (CYCLE_ACTIVE_IDS.has(latest.id)) {
    return {
      status: latest.payload?.status || 'developing',
      pivotType: latest.payload?.pivotType || 'provisional',
      pivotIndex: maxPivotIndex,
      pivotTime: latest.payload?.pivotTime ?? latest.t ?? null,
      [priceKey]: latest.payload?.price ?? null,
      pivotConfidencePct: latest.payload?.pivotConfidencePct ?? 100,
      confidencePct: latest.payload?.confidencePct ?? latest.payload?.pivotConfidencePct ?? 100,
    };
  }
  // Unknown event id — defensive: treat as "nothing to render"
  // rather than entering render path with partial state.
  return null;
};

/**
 * Fetches persisted cycle events for a symbol/timeframe off the backend
 * /events endpoint and shapes them for the chart's cycle overlays.
 *
 * Returns the same array shapes the existing overlays already consume
 * (`liveLowEvents` / `liveHighEvents` as library-format events for the
 * dot-marker pane; `confirmedLows` / `confirmedHighs` as pivot rows for
 * the "CL N" arrow triangles). This hook is the chart's ONLY source of
 * cycle pivots — the browser computes none (DWLF-113 ②b).
 *
 * The fetch re-runs on (symbol, timeframe), a `daysBack` change or
 * `refetch()`; there is no mode in which backend data should not be
 * used. Cached events reset only on a (symbol, timeframe) change.
 */
// Exported for unit tests. Not part of the public hook API; feel free to
// change shape as long as you update the tests alongside.
export const __test__ = {
  toLibraryEvent,
  buildConfirmedPivots,
  buildLiveState,
  TIMEFRAME_UI_TO_CANONICAL,
};

// Per-timeframe fetch windows for the events history. Cycle cadence
// scales with the timeframe — a daily cycle fires every ~30-60 days,
// a weekly every ~30-50 weeks, an hourly every ~25-50 hours — so the
// fetch window has to scale too or the chart's backend view starves
// of historical pivots.
//
// Sized to comfortably cover the chart's typical zoomed-out view at
// each timeframe. Daily is deliberately tighter than weekly because
// daily emits many more lifecycle events per cycle (provisional +
// developing.33/66/100 on basically every bar until confirmation),
// and the paging cap below (25 × 200 = 5000 events) caps us around
// ~12y of daily traffic — going wider on daily risks silently
// dropping the oldest events. Weekly at 21y matches the chart's 1040
// weekly candles (DWLF-424) and stays well under the cap because weekly
// cycles are ~10× slower. Hourly keeps its legacy 1y
// window — the lambda's wider-window backfill caps at ~14mo anyway.
//
// Caller can still override `daysBack` for tests or special views.
const DEFAULT_DAYS_BACK_BY_TIMEFRAME = {
  '1w': 21 * 365,
  '1d': 10 * 365,
  '1h': 365,
};

export const useBackendCycleEvents = ({ symbol, timeframe, daysBack }) => {
  const [rawEvents, setRawEvents] = useState([]);
  // Seeded with `true` so the very first render reports "loading" to
  // consumers, matching the fact that our fetch effect is about to
  // fire. Without this, the initial render paints with loading=false
  // AND rawEvents=[], which the consumer's gate cannot distinguish
  // from "fetch completed with zero events" — triggering a frame of
  // client-side cycle computation before the fetch kicks in. That
  // single frame of jitter on every mount is exactly what the
  // backend-by-default refactor was meant to kill. The fetch effect
  // below still flips this to true explicitly when the fetch starts
  // and false when it completes / short-circuits, so steady-state
  // behaviour is unchanged.
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // `refetchToken` is just a monotonically increasing number wired into
  // the fetch effect's deps. Bumping it triggers a fresh run with the
  // same (symbol, timeframe, daysBack) — what `refetch()` returns does
  // for the caller. Used by the chart page's 15-min polling effect so
  // a user parked on the chart sees fresh events without manually
  // refreshing.
  const [refetchToken, setRefetchToken] = useState(0);
  const refetch = useCallback(() => {
    setRefetchToken((prev) => prev + 1);
  }, []);
  const requestSeqRef = useRef(0);

  const canonicalTimeframe = TIMEFRAME_UI_TO_CANONICAL[timeframe] || timeframe || null;
  const effectiveDaysBack = Number.isFinite(daysBack)
    ? daysBack
    : (DEFAULT_DAYS_BACK_BY_TIMEFRAME[canonicalTimeframe] ?? 365);

  // Reset cached events ONLY when (symbol, timeframe) actually changes —
  // its own effect, so nothing else that re-runs the fetch effect
  // (a refetch token, a days-back change) throws away good cached data
  // and blanks the chart for a frame.
  // Still clears on symbol / TF change so we can't briefly show
  // AAPL pivots on NVDA's chart. (Bugbot caught this on review.)
  // Tracks whether we've completed at least one fetch for the current
  // (symbol, timeframe). Reset on symbol/tf change; flipped true in the
  // fetch effect's finally-block. The fetch effect consults this before
  // flipping `loading` to true so background refetches (same symbol,
  // events already on-screen) don't momentarily fail the consumer's
  // "!loading" gate and cause a frame of client-side-detector jitter.
  const hasSettledRef = useRef(false);
  useEffect(() => {
    setRawEvents([]);
    hasSettledRef.current = false;
  }, [symbol, canonicalTimeframe]);

  useEffect(() => {
    if (!symbol || !canonicalTimeframe) {
      // If we've decided not to fetch, loading must clear, otherwise the
      // initial-true state (set to keep the first render from
      // falsely short-circuiting as "loaded empty") would latch on
      // forever for any consumer that mounts with a null symbol.
      setLoading(false);
      return undefined;
    }

    const seq = requestSeqRef.current + 1;
    requestSeqRef.current = seq;

    let cancelled = false;
    // Only flip `loading` to true when we don't yet have settled data
    // for this (symbol, timeframe). Background refetches (15-min poll
    // or tab-focus refresh) keep loading=false so the consumer's gate
    // keeps rendering the stale-but-present events instead of blanking
    // out for a frame and falling through to client-side computation.
    // New events arrive via setRawEvents at the end of the fetch.
    if (!hasSettledRef.current) {
      setLoading(true);
    }
    setError(null);

    (async () => {
      try {
        const pages = [];
        let cursor;
        // Bounded page-count so a pathological dataset can't lock the
        // UI into infinite paging. 25 × 200 = 5000 events covers ~12y
        // of daily lifecycle events both sides (~75 confirmed × 10
        // lifecycle siblings × 2 sides = ~1500 events, plus headroom)
        // and the 21y weekly window. Hourly's 365d window keeps it well under
        // even the old 10-page cap.
        //
        // sortOrder MUST be 'desc'. The backend's multi-event-type path
        // (eventsService.getRecentEvents lines 371-398) runs one query
        // per event type, flat-maps the results, sorts, and slices to
        // `limit` AFTER merging. With 'asc' the slice keeps the oldest
        // 200 — and since cycle.low.provisional / developing.* fire
        // on nearly every bar, the 29 confirmed events we actually
        // care about get pushed out of every page and the chart never
        // sees the latest cycle.low.confirmed at the current bar.
        // 'desc' keeps the most recent 200, which always includes the
        // confirmed-by-pivot events the chart needs. The chart re-sorts
        // by pivotIndex downstream, so the order off the wire doesn't
        // matter.
        for (let i = 0; i < 25; i += 1) {
          // eslint-disable-next-line no-await-in-loop
          const response = await eventsAPI.getRecentEvents({
            scope: 'system',
            symbol,
            timeframe: canonicalTimeframe,
            eventTypes: CYCLE_EVENT_TYPES,
            days: effectiveDaysBack,
            limit: 200,
            sortOrder: 'desc',
            cursor: cursor || undefined,
          });
          pages.push(...(response?.events || []));
          cursor = response?.cursor || response?.nextToken;
          if (!cursor) {break;}
        }
        if (cancelled || seq !== requestSeqRef.current) {return;}
        setRawEvents(pages);
      } catch (err) {
        if (cancelled || seq !== requestSeqRef.current) {return;}
        setError(err);
        // Only clear the cached events when we haven't yet settled —
        // i.e. initial failure with nothing on screen. For background
        // refetches (15-min poll, tab-focus refresh), keep the
        // previously-good events visible so a transient network blip
        // doesn't blank the chart's backend overlays and flip the
        // consumer's gate into client-side computation. Error still
        // surfaces via `error`; the next successful refetch replaces
        // the stale set.
        if (!hasSettledRef.current) {
          setRawEvents([]);
        }
        // eslint-disable-next-line no-console
        console.warn('useBackendCycleEvents: fetch failed', { symbol, timeframe: canonicalTimeframe, err });
      } finally {
        if (!cancelled && seq === requestSeqRef.current) {
          hasSettledRef.current = true;
          setLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [symbol, canonicalTimeframe, effectiveDaysBack, refetchToken]);

  const shaped = useMemo(() => {
    const libraryEvents = rawEvents.map(toLibraryEvent).filter(Boolean);
    const liveLowEvents = libraryEvents.filter((e) => e.id.startsWith('cycle.low.'));
    const liveHighEvents = libraryEvents.filter((e) => e.id.startsWith('cycle.high.'));
    const confirmedLows = buildConfirmedPivots(liveLowEvents, 'low');
    const confirmedHighs = buildConfirmedPivots(liveHighEvents, 'high');
    const liveLowState = buildLiveState(liveLowEvents, 'low');
    const liveHighState = buildLiveState(liveHighEvents, 'high');
    return {
      liveLowEvents,
      liveHighEvents,
      confirmedLows,
      confirmedHighs,
      liveLowState,
      liveHighState,
    };
  }, [rawEvents]);

  return {
    ...shaped,
    loading,
    error,
    refetch,
  };
};

export default useBackendCycleEvents;
